/**
 * LanguagePicker — Full-screen language selector modal
 * Triggered from top-right corner button (next to theme toggle)
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Globe } from 'lucide-react';
import { useLanguage, LANGUAGES } from '../contexts/LanguageContext';

// ── Regional groupings for display ────────────────────────────
const GROUPS = [
  {
    label: 'Américas & Europa Occidental',
    codes: ['es', 'en', 'pt', 'fr', 'de', 'it', 'nl'],
  },
  {
    label: 'Asia & Medio Oriente',
    codes: ['ja', 'zh', 'ko', 'ar'],
  },
  {
    label: 'Europa Oriental & Otros',
    codes: ['ru', 'tr', 'pl', 'id'],
  },
];

// Map each lang to its gradient accent color
const ACCENTS = {
  es: '#c0392b', en: '#3b82f6', pt: '#16a34a', fr: '#6366f1',
  de: '#374151', it: '#dc2626', nl: '#f97316', ja: '#ec4899',
  zh: '#dc2626', ko: '#6366f1', ar: '#16a34a', ru: '#3b82f6',
  tr: '#dc2626', pl: '#dc2626', id: '#dc2626',
};

export default function LanguagePicker({ onClose }) {
  const { language, setLanguage, t } = useLanguage();
  const [selected, setSelected] = useState(language);
  const [animating, setAnimating] = useState(false);
  const [visible, setVisible] = useState(false);
  const overlayRef = useRef(null);

  // Entrance animation
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280);
  };

  const handleSelect = (code) => {
    if (code === selected) { handleClose(); return; }
    setSelected(code);
    setAnimating(true);
    setTimeout(() => {
      setLanguage(code);
      setAnimating(false);
      setTimeout(handleClose, 200);
    }, 300);
  };

  // Close on backdrop click
  const handleBackdrop = (e) => {
    if (e.target === overlayRef.current) handleClose();
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  return createPortal(
    <div
      ref={overlayRef}
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'opacity 0.28s ease, backdrop-filter 0.28s ease',
        opacity: visible ? 1 : 0,
        padding: '16px',
      }}
    >
      {/* Modal card */}
      <div
        style={{
          width: '100%', maxWidth: '780px',
          background: 'linear-gradient(145deg, #111114 0%, #0d0d10 100%)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '28px',
          boxShadow: '0 40px 120px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04) inset',
          overflow: 'hidden',
          transition: 'transform 0.28s cubic-bezier(0.34,1.56,0.64,1), opacity 0.28s ease',
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.94) translateY(24px)',
          opacity: visible ? 1 : 0,
          maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '28px 32px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            {/* Animated globe icon */}
            <div style={{
              width: 48, height: 48,
              background: 'linear-gradient(135deg, rgba(220,38,38,0.2), rgba(220,38,38,0.05))',
              border: '1px solid rgba(220,38,38,0.25)',
              borderRadius: '14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Globe size={22} color="#ef4444" />
            </div>
            <div>
              <p style={{ color: '#fff', fontWeight: 700, fontSize: '20px', margin: 0, lineHeight: 1.2 }}>
                Idioma / Language
              </p>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: '3px 0 0', lineHeight: 1 }}>
                Activo: <span style={{ color: 'rgba(255,255,255,0.7)' }}>{currentLang.flag} {currentLang.nativeName}</span>
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '10px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
              color: 'rgba(255,255,255,0.6)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
          >
            <X size={18} />
          </button>
        </div>

        {/* Language grid — scrollable */}
        <div style={{
          overflowY: 'auto', padding: '24px 28px 28px',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.1) transparent',
        }}>
          {GROUPS.map((group) => (
            <div key={group.label} style={{ marginBottom: '28px' }}>
              <p style={{
                color: 'rgba(255,255,255,0.25)', fontSize: '11px',
                fontWeight: 600, letterSpacing: '1.2px',
                textTransform: 'uppercase', margin: '0 0 12px 4px',
              }}>
                {group.label}
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: '10px',
              }}>
                {group.codes.map(code => {
                  const lang = LANGUAGES.find(l => l.code === code);
                  if (!lang) return null;
                  const isActive = language === code;      // idioma actual en contexto
                  const isSelected = selected === code;   // selección pendiente (feedback inmediato)
                  // Highlight inmediato al hacer clic (isSelected), y después el contexto confirma (isActive)
                  const isHighlighted = isSelected || isActive;
                  const accent = ACCENTS[code] || '#ef4444';

                  return (
                    <button
                      key={code}
                      onClick={() => handleSelect(code)}
                      style={{
                        background: isHighlighted
                          ? `linear-gradient(135deg, ${accent}22, ${accent}0a)`
                          : 'rgba(255,255,255,0.03)',
                        border: isHighlighted
                          ? `1px solid ${accent}55`
                          : '1px solid rgba(255,255,255,0.07)',
                        borderRadius: '16px',
                        padding: '14px 16px',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '12px',
                        transition: 'all 0.18s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        textAlign: 'left',
                        width: '100%',
                      }}
                      onMouseEnter={e => {
                        if (!isHighlighted) {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                          e.currentTarget.style.border = '1px solid rgba(255,255,255,0.14)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isHighlighted) {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                          e.currentTarget.style.border = '1px solid rgba(255,255,255,0.07)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      {/* Flag */}
                      <span style={{
                        fontSize: '28px', lineHeight: 1,
                        filter: isHighlighted ? 'none' : 'grayscale(20%)',
                        transition: 'filter 0.18s',
                        flexShrink: 0,
                      }}>
                        {lang.flag}
                      </span>

                      {/* Names */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                          color: isHighlighted ? '#fff' : 'rgba(255,255,255,0.75)',
                          fontSize: '14px', fontWeight: 600,
                          margin: 0, lineHeight: 1.2,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {lang.nativeName}
                        </p>
                        <p style={{
                          color: 'rgba(255,255,255,0.35)', fontSize: '11px',
                          margin: '2px 0 0', lineHeight: 1,
                        }}>
                          {lang.label}
                        </p>
                      </div>

                      {/* Check badge — visible inmediatamente al seleccionar */}
                      {isHighlighted && (
                        <div style={{
                          width: 20, height: 20,
                          background: isActive ? accent : 'rgba(255,255,255,0.25)',
                          borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'background 0.3s',
                        }}>
                          <Check size={11} color="#fff" strokeWidth={3} />
                        </div>
                      )}

                      {/* Glow accent */}
                      {isHighlighted && (
                        <div style={{
                          position: 'absolute', top: -20, right: -20,
                          width: 80, height: 80,
                          background: accent,
                          opacity: 0.06,
                          borderRadius: '50%',
                          filter: 'blur(20px)',
                          pointerEvents: 'none',
                        }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div style={{
          padding: '14px 32px',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '12px', margin: 0 }}>
            El idioma aplica a toda la plataforma y las respuestas del chat · Esc para cerrar
          </p>
        </div>
      </div>

      {/* Loading overlay */}
      {animating && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(8px)',
            borderRadius: '16px', padding: '20px 32px',
            color: '#fff', fontSize: '15px', fontWeight: 500,
          }}>
            ✓ Aplicando idioma…
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
